import * as React from "react";
import { useEffect, useState, useRef } from "react";

import { useAppDispatch, useAppSelector } from "../../hooks/reduxhook";

import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import {
  addImageModels,
  addModel,
  getModelDetails,
} from "../../redux/slices/modelSlice";
import { getType } from "../../redux/slices/typeSlice";
import hyperscaler_mapping from "../../data/modelMapping";

import { useTheme } from "@mui/material";

export default function SelectModels(type: any) {
  const model_user_details = useAppSelector(getModelDetails);
  const chatType = useAppSelector(getType);
  const theme = useTheme();

  const dispatch = useAppDispatch();

  const [localModel, setLocalModel] = useState<string | undefined>(model_user_details?.model);
  const [localImageModel, setLocalImageModel] = useState<string | undefined>(model_user_details?.imagemodel);

  const selectDefaultModel = () => {
    const models = model_user_details?.models?.[chatType.mode?.[0] ?? "chat"] ?? [];
    const sortedModels = [...models].sort(sortGPTfirst);
    
    if (sortedModels.length === 0) {
      console.error("No models available to select from");
      return;
    }
    
    const modelName = sortedModels[1].model_name;
    const isRegularModel = type.type === undefined;
    
    dispatch(isRegularModel ? addModel(modelName) : addImageModels(modelName));
    isRegularModel ? setLocalModel(modelName) : setLocalImageModel(modelName);
  };

  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      selectDefaultModel();
      isFirstRender.current = false;
      return;
    }

    if (!model_user_details?.model && !model_user_details?.imagemodel) {
      selectDefaultModel();
    }

    if (model_user_details?.model) {
      setLocalModel(model_user_details.model);
    }
    if (model_user_details?.imagemodel) {
      setLocalImageModel(model_user_details.imagemodel);
    }
  }, [model_user_details, chatType.mode, type.type, dispatch, setLocalModel, setLocalImageModel]);

  const handleChange = (event: SelectChangeEvent) => {
    if (type.type === undefined) {
      dispatch(addModel(event.target.value));
    } else {
      dispatch(addImageModels(event.target.value));
    }
  };

  interface Model {
    model_name: string;
    model: string;
  }

  interface ModelDetails {
    model: string;
    imagemodel: string;
    models: Record<string, Model[]>;
  }

  interface ChatType {
    mode: string[];
  }

  const sortGPTfirst = (firstModel: Model, secondModel: Model): number => {
    const isGptModel = (firstModel?.model_name || "")
      .toLowerCase()
      .includes("gpt");
    const isOtherModel = (secondModel?.model_name || "")
      .toLowerCase()
      .includes("gpt");
    if (isGptModel && !isOtherModel) return -1;
    if (!isGptModel && isOtherModel) return 1;
    return (firstModel?.model_name || "").localeCompare(
      secondModel?.model_name || ""
    );
  };

  if (!model_user_details || !chatType.mode) {
    return <div>Error: Missing required data</div>;
  }

  return (
    <FormControl
      variant="outlined"
      sx={{
        width: "100%",
      }}
    >
      <Select
        value={
          type?.type === undefined
            ? localModel ?? ""
            : localImageModel ?? ""
        }
        onChange={handleChange}
        size="small"
        sx={{
          width: "100%",
          fontSize: "11px",
          border: "1px solid #CBCBCB",
          backgroundColor: theme.palette.mode === "dark" ? "#111" : "#F3F3F3",
          borderRadius: "0px",
          textTransform: "capitalize",
          "& .MuiSelect-select": {
            display: "flex !important",
            alignItems: "center",
            gap: "8px",
            width: "100%",
            "& > *:not(img)": {
              flex: "1",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }
          },
          "& .MuiPaper-rounded": {
            borderRadius: "0px",
          },
        }}
      >
        {chatType.mode?.map((mode, i) => {
          const models = model_user_details?.models[`${mode ?? "chat"}`] || [];
          const sortedModels = [...models].sort(sortGPTfirst);
          return sortedModels.map((model, j) => (
            <MenuItem
              key={`${i}-${j}`}
              value={model?.model_name}
              sx={{
                "&.Mui-selected": {
                  backgroundColor: "#FFD2B9",
                },
                textTransform: "capitalize",
                whiteSpace: "normal",
                wordBreak: "break-word",
                minHeight: "auto",
                padding: "8px",
              }}
            >
              <img
                src={
                  hyperscaler_mapping[`${model?.model?.split("/")[0] ?? "psl"}`] || 
                  hyperscaler_mapping["default"]
                }
                alt={`${model?.model_name}`}
                width="15px"
              />
              <span>
                {model?.model_name
                  .replaceAll(
                    process.env?.REACT_APP_DEFAULT_MODEL_REMOVE ?? "",
                    ""
                  )
                  .replaceAll("-", " ")
                  .replaceAll("AI_Team", "")
                  .replaceAll("iAura_team", "iAura")
                }
              </span>
            </MenuItem>
          ));
        })}
      </Select>
    </FormControl>
  );
}